import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ZenObservable } from 'zen-observable-ts';

import { appConstants } from '../../../shared/constants/constants';

import { APIService, Envasado } from '../../../app-sync.service';
import { EnvasadosService } from '../envasados.service';
import { UsersService } from '../../users/users.service';
import { DataTableDirective } from 'angular-datatables';
import { DocumentsService } from '../../documents/documents.service';
import { MasterService } from '../../master/master.service';

@Component({
  selector: 'app-envasados-list',
  templateUrl: './envasados-list.component.html',
  styleUrls: ['./envasados-list.component.css'],
})
export class EnvasadosListComponent implements OnInit, OnDestroy {
  entityName: string = 'Envasados Rígidos';
  dtOptions: DataTables.Settings = {};
  isGettingEnvasados: boolean = false;
  isAdmin: boolean = false;
  isApprover: boolean = false;
  isViewer: boolean = false;
  isCarrier: boolean = false;
  envasados: Envasado[];
  envasadosAttributes: string[] = [
    'SAP ID',
    'Estado',
    'Patente',
    'Transportista',
    'Centro',
    'Alertas',
    'Bloqueos',
  ];

  //Esto permite crear un filtro de búsqueda personalizado en el datatable
  searchText: string = '';
  @ViewChild(DataTableDirective, { static: false })
  dtElement!: DataTableDirective;

  private envasadosChangedSubscription: Subscription = new Subscription();
  private onDeleteEnvasadoListener: ZenObservable.Subscription =
    new Subscription();
  private onCreateEnvasadoListener: ZenObservable.Subscription =
    new Subscription();
  private onUpdateEnvasadoListener: ZenObservable.Subscription =
    new Subscription();

  constructor(
    private envasadosService: EnvasadosService,
    private api: APIService,
    private router: Router,
    private route: ActivatedRoute,
    private usersService: UsersService,
    private documentService: DocumentsService,
    private masterService: MasterService
  ) {
    this.dtOptions = appConstants.datatables.options;
    this.envasados = this.envasadosService.getEnvasados();
  }

  envasadosActivos: boolean = false;

  async ngOnInit(): Promise<void> {
    this.envasadosChangedSubscription =
      this.envasadosService.envasadosChanged.subscribe(
        (envasados: Envasado[]) => {
          this.envasados = envasados;
        }
      );

    this.isAdmin = this.usersService.isAdmin;
    this.isApprover = this.usersService.isApprover;
    this.isViewer = this.usersService.isViewer;
    this.isCarrier = this.usersService.isCarrier;

    await this.updateEnvasadosList();

    // Una vez cargada la lista de vehículos definimos los filtros para subscripciones
    const subscriptionEnvasadosFilter =
      this.envasadosService.getEnvasadosFilterForSubscriptions();
    // Subscripción a Vehículos eliminados
    this.onDeleteEnvasadoListener = this.api
      .OnDeleteEnvasadoListener(subscriptionEnvasadosFilter)
      .subscribe((response) => {
        if (response) {
          this.envasadosService.refreshEnvasados();
        }
      });
    // Subscripción a Vehículos actualizados
    this.onUpdateEnvasadoListener = this.api
      .OnUpdateEnvasadoListener(subscriptionEnvasadosFilter)
      .subscribe((response) => {
        if (response) {
          this.envasadosService.refreshEnvasados();
        }
      });
    // Subscripción a Vehículos creados
    this.onCreateEnvasadoListener = this.api
      .OnCreateEnvasadoListener(subscriptionEnvasadosFilter)
      .subscribe((response) => {
        if (response) {
          this.envasadosService.refreshEnvasados();
        }
      });
  }

  /**
   * Navega a al formulario de creación de Envasado Rígido.
   */
  onNewEnvasado(): void {
    this.envasadosActivos = !this.envasadosActivos;
    this.router
      .navigate(['new'], { relativeTo: this.route })
      .then(() => console.log('navigate to new'));
  }

  /**
   * Llama al método que refresca la lista de Envasados Rígidos.
   * @return {Promise}
   */
  async onRefresh(): Promise<void> {
    this.searchText = '';
    await this.updateEnvasadosList();
  }

  /**
   * Define el envasado rígido que está seleccionado.
   * y navega al detalle del mismo.
   * @param {Envasado} envasado Envasado rígido seleccionado de la lista.
   */
  onSelectedEnvasado(envasado: Envasado): void {
    this.documentService.isRefreshFilter.next(true);
    this.envasadosService.setSelectedEnvasado(envasado);
    this.router
      .navigate([envasado.envasadoId], { relativeTo: this.route })
      .then(() => console.log(`navigate to ${envasado.envasadoId}`));
  }

  /**
   * Indica si un envasado rígido tiene documentos por vencer.
   * @param {Envasado} envasado Envasado rígido de la lista.
   * @return {boolean} Boolean que indica si el Envasado rígido tiene documentos por vencer.
   */
  hasToExpireDocuments(envasado: Envasado): boolean {
    const docNumber = envasado.documentsToExpire || 0;
    return docNumber > 0;
  }

  /**
   * Indica si un envasado rígido tiene documentos vencidos.
   * @param {Envasado} envasado Envasado rígido de la lista.
   * @return {boolean} Boolean que indica si el Envasado rígido tiene documentos vencidos.
   */
  hasExpiredDocuments(envasado: Envasado): boolean {
    const docNumber = envasado.documentsExpired || 0;
    return docNumber > 0;
  }

  /**
   * Retorna la fuente de imagen a mostrar en el Estado del Envasado Rígido.
   * @return {string} ruta a imagen.
   */
  getImageSrc(status: string): string {
    return this.masterService.getImageSrc(status);
  }

  /**
   * Refresca la lista de envasados rígidos.
   * @private
   * @return {Promise}
   */
  private async updateEnvasadosList(): Promise<void> {
    this.isGettingEnvasados = true;
    await this.envasadosService.refreshEnvasados().then((): void => {
      this.isGettingEnvasados = false;
    });
  }

  /**
   * Determina, con base al rol del usuario, cuáles botones mostrar.
   * @param {string} button Acción del botón.
   * @return {boolean}
   */
  showButton(button: string): boolean {
    let show: boolean = false;

    if (button === 'create') {
      show = this.isAdmin || this.isCarrier;
    }
    return show;
  }

  /**
   * Determina, con base al rol del usuario, si debe ocultarse una columna de la tabla.
   * @param {string} column Nombre de la columna.
   * @return {Boolean}
   */
  hideColumn(column: string): boolean {
    let hide: boolean = false;

    if (column === 'Transportista') {
      hide = !(this.isAdmin || this.isApprover || this.isViewer);
    }
    return hide;
  }

  /**
   * Aplica a la tabla el filtro ingresado por el usuario.
   */
  applyFilter(): void {
    if (this.dtElement) {
      this.dtElement.dtInstance.then((dtInstance: DataTables.Api): void => {
        dtInstance.search(this.searchText).draw();
      });
    }
  }

  ngOnDestroy(): void {
    this.envasadosChangedSubscription.unsubscribe();
    this.onDeleteEnvasadoListener.unsubscribe();
    this.onCreateEnvasadoListener.unsubscribe();
    this.onUpdateEnvasadoListener.unsubscribe();
    console.log('envasados-list.component subscriptions removed.');
  }
}
